import React, { useMemo, useRef, useState } from 'react';
import Modal from './Modal';
import resumeData from '../data/resumeData.json';

/**
 * @typedef {Object} ExperienceRole
 * @property {string} id
 * @property {string} title
 * @property {string} company
 * @property {string} period
 * @property {string} description
 * @property {string} [shortDescription]
 * @property {string} [location]
 */

/**
 * @typedef {Object} ExperienceCompany
 * @property {string} id
 * @property {string} company
 * @property {string} location
 * @property {string} [logo]
 * @property {string} [logoAlt]
 * @property {ExperienceRole[]} roles
 */

const modalBodyStyle = {
  maxHeight: 'min(70vh, 560px)',
  overflowY: 'auto',
  lineHeight: 1.7,
  color: '#2b3443'
};

const emptyDescriptionText =
  'Detailed responsibilities and achievements are currently unavailable for this role.';

/**
 * Usage note:
 * <Experience data={{ heading, description, items: [{ id, company, location, logo, roles: [...] }] }} />
 */
const Experience = ({ data = resumeData.experience }) => {
  const experiences = useMemo(() => {
    if (Array.isArray(data?.items) && data.items.length > 0) {
      return data.items;
    }

    return resumeData.experience.items;
  }, [data]);

  const [selectedRole, setSelectedRole] = useState(null);
  const triggerRefs = useRef({});
  const lastTriggerIdRef = useRef(null);

  const openExperienceModal = (role) => {
    if (!role) {
      return;
    }

    lastTriggerIdRef.current = role.id;
    setSelectedRole(role);
  };

  const closeExperienceModal = () => {
    setSelectedRole(null);

    // Restore focus to the exact role card that opened the modal.
    window.requestAnimationFrame(() => {
      const triggerId = lastTriggerIdRef.current;

      if (triggerId && triggerRefs.current[triggerId]) {
        triggerRefs.current[triggerId].focus();
      }
    });
  };

  return (
    <section className="o-section t-section">
      <div className="o-section__header-bg t-section__header"></div>
      <div className="o-section__content-bg t-section__content"></div>

      <div className="o-container">
        <div className="o-section__container">
          <header className="o-section__header t-section__header">
            <div className="o-content">
              <h2 className="o-section__heading">{data.heading}</h2>
              <div className="o-content__body o-section__description">{data.description}</div>
            </div>
          </header>

          <div className="o-section__content t-section__content u-pb-0">
            <div className="a-experience-timeline c-timeline t-border-color">
              {experiences.map((companyExperience) => (
                <div className="c-timeline__item" key={companyExperience.id}>
                  <div className="c-timeline__point t-timeline__point t-primary-bg"></div>
                  <div className="o-content">
                    <div className="experience-company-card">
                      <div className="experience-company-header">
                        <div className="experience-company-meta">
                          {companyExperience.logo ? (
                            <span className="experience-company-logo-wrap" aria-hidden="true">
                              <img
                                className="experience-company-logo"
                                src={companyExperience.logo}
                                alt={companyExperience.logoAlt || `${companyExperience.company} logo`}
                                loading="lazy"
                              />
                            </span>
                          ) : null}
                          <div>
                            <h3 className="c-work__heading experience-company-name">{companyExperience.company}</h3>
                            <div className="c-work__location">{companyExperience.location}</div>
                          </div>
                        </div>
                      </div>

                      <div className="experience-role-list">
                        {companyExperience.roles.map((role, roleIndex) => (
                          <button
                            key={role.id}
                            ref={(element) => {
                              triggerRefs.current[role.id] = element;
                            }}
                            type="button"
                            onClick={() => openExperienceModal(role)}
                            className="experience-role-button"
                            aria-haspopup="dialog"
                            aria-controls="experience-details-modal"
                          >
                            <div className="o-grid">
                              <div className="o-grid__col-md-5">
                                <div className="c-work__timeframe">{role.period}</div>
                                <h4 className="c-work__title experience-role-title">{role.title}</h4>
                                <div className="c-work__location">{role.location || companyExperience.location}</div>
                              </div>
                              <div className="o-grid__col-md-7">
                                <p>{role.shortDescription || role.description || emptyDescriptionText}</p>
                              </div>
                            </div>
                            {roleIndex < companyExperience.roles.length - 1 ? (
                              <span className="experience-role-divider" aria-hidden="true"></span>
                            ) : null}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Modal
        id="experience-details-modal"
        isOpen={Boolean(selectedRole)}
        onRequestClose={closeExperienceModal}
        title={selectedRole ? `${selectedRole.company} — ${selectedRole.title}` : ''}
        width="760px"
      >
        <div style={modalBodyStyle}>
          <p>
            <strong>Period:</strong> {selectedRole?.period || '-'}
          </p>
          <p>
            <strong>Location:</strong> {selectedRole?.location || '-'}
          </p>
          {Array.isArray(selectedRole?.description) ? (
            <ul style={{ paddingLeft: '20px', margin: 0 }}>
              {selectedRole.description.map((point, i) => (
                <li key={i} style={{ marginBottom: '8px' }}>{point}</li>
              ))}
            </ul>
          ) : (
            <p>{selectedRole?.description || emptyDescriptionText}</p>
          )}
        </div>
      </Modal>

      <style>{`
        .a-experience-timeline .c-timeline__item {
          margin-bottom: 24px;
        }

        .a-experience-timeline .c-timeline__point {
          top: 50%;
          transform: translateY(-50%);
        }

        .experience-company-card {
          border: 1px solid rgba(30, 48, 80, 0.14);
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.94);
          box-shadow: 0 10px 24px -22px rgba(11, 31, 54, 0.75);
        }

        .experience-company-header {
          padding: 14px 16px;
          border-bottom: 1px solid rgba(20, 38, 63, 0.1);
        }

        .experience-company-meta {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .experience-company-logo-wrap {
          width: 36px;
          height: 36px;
          flex: 0 0 36px;
          border-radius: 9px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #ffffff;
          border: 1px solid rgba(20, 38, 63, 0.12);
          box-shadow: 0 8px 20px -18px rgba(10, 22, 40, 0.8);
        }

        .experience-company-logo {
          max-width: 24px;
          max-height: 24px;
          object-fit: contain;
        }

        .experience-company-name {
          margin-bottom: 6px;
        }

        .experience-role-list {
          display: flex;
          flex-direction: column;
        }

        .experience-role-button {
          width: 100%;
          text-align: left;
          border: none;
          border-radius: 0;
          padding: 16px;
          background: transparent;
          transition: background-color 180ms ease;
          cursor: pointer;
          position: relative;
        }

        .experience-role-button:hover {
          background: rgba(245, 248, 252, 0.95);
        }

        .experience-role-button:focus-visible {
          outline: 3px solid rgba(25, 117, 209, 0.35);
          outline-offset: -3px;
        }

        .experience-role-title {
          font-size: 0.95rem;
          color: #1d2e45;
          margin-bottom: 6px;
        }

        .experience-role-divider {
          position: absolute;
          left: 16px;
          right: 16px;
          bottom: 0;
          height: 1px;
          background: rgba(20, 38, 63, 0.12);
        }

        @media (max-width: 768px) {
          .experience-company-header,
          .experience-role-button {
            padding: 13px 12px;
          }

          .experience-company-logo-wrap {
            width: 32px;
            height: 32px;
            flex-basis: 32px;
          }

          .experience-company-logo {
            max-width: 20px;
            max-height: 20px;
          }

          .a-experience-timeline .c-timeline__item {
            margin-bottom: 18px;
          }
        }
      `}</style>

      {/*
        Manual test checklist:
        1) The timeline blue dots align close to vertical center of each company card.
        2) Click any role entry -> modal opens with full description for that role.
        3) In-card text shows short descriptions; modal shows long descriptions.
        4) Esc/backdrop/close button close the modal and focus returns to clicked role.
        5) Multi-role companies render as grouped cards (promotion flow similar to LinkedIn).
      */}
    </section>
  );
};

export default Experience;
